'use client'

import { useCart, useLocationMetadata, useModals, useOrderProperties } from '@/lib/hooks'
import React from 'react'
import { toast } from 'sonner'
import { useQuery } from '@tanstack/react-query'
import { getLocations } from '@/lib/actions/global/get-locations'

import { Button, DateTimePicker, Label, DropdownMenu, DropdownMenuContent, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuTrigger } from '@/components/ui'
import { CheckCircle, Loader2, ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'


interface SetOrderPropertiesProps extends React.ComponentPropsWithoutRef<'form'> {
  onClose: () => void
  cartId: string
  onSuccess?: () => void | Promise<void>
}

export const SetOrderProperties = ({ onClose, cartId, onSuccess }: SetOrderPropertiesProps) => {
  const [isPending, startTransition] = React.useTransition()
  const { closeModal } = useModals()
  const { updateOrderProperties } = useOrderProperties()
  const { cart } = useCart()
  const {
    order_sources,
    delivery_methods,
    future_order_types,
    setLocationMetadata,
  } = useLocationMetadata()

  // Track if user has manually changed values (use ref to avoid re-renders)
  const userModifiedRef = React.useRef({
    pickupTime: false,
    fulfillmentTime: false,
    orderSource: false,
    deliveryMethod: false,
  })

  // Pre-populate with cart data if available, otherwise leave empty
  const [pickupTime, setPickupTime] = React.useState<string>(() =>
    cart?.cart?.pickup_time || ''
  )
  const [fulfillmentTime, setFulfillmentTime] = React.useState<string>(() =>
    cart?.cart?.fulfillment_time || ''
  )

  const [orderSource, setOrderSource] = React.useState<string>(() =>
    cart?.cart?.order_source || ''
  )
  const [deliveryMethod, setDeliveryMethod] = React.useState<string>(() =>
    cart?.cart?.delivery_method || ''
  )

  // Sync state when cart data becomes available (but don't overwrite user changes)
  React.useEffect(() => {
    if (cart?.cart) {
      if (cart.cart.pickup_time && !userModifiedRef.current.pickupTime) {
        setPickupTime(cart.cart.pickup_time)
      }
      if (cart.cart.fulfillment_time && !userModifiedRef.current.fulfillmentTime) {
        setFulfillmentTime(cart.cart.fulfillment_time)
      }
      if (cart.cart.order_source && !userModifiedRef.current.orderSource) {
        setOrderSource(cart.cart.order_source)
      }
      if (cart.cart.delivery_method && !userModifiedRef.current.deliveryMethod) {
        setDeliveryMethod(cart.cart.delivery_method)
      }
    }
  }, [cart])

  // Fetch location metadata if not already loaded
  const { data: locations, isLoading: isLoadingLocations } = useQuery({
    queryKey: ['locations'],
    queryFn: () => getLocations(),
    enabled: !order_sources || !delivery_methods, // Only fetch if not already in Redux
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  // Update Redux store when data is fetched
  React.useEffect(() => {
    if (locations && (!order_sources || !delivery_methods)) {
      setLocationMetadata({
        order_sources: locations.order_sources ?? [],
        delivery_methods: locations.delivery_methods ?? [],
        future_order_types: locations.future_order_types ?? [],
        business_settings: locations.business_settings ?? [],
      })
    }
  }, [locations, order_sources, delivery_methods, setLocationMetadata])

  // Check if metadata is loaded
  const isMetadataLoaded = order_sources && delivery_methods
  const hasOrderSources = order_sources && order_sources.length > 0
  const hasDeliveryMethods = delivery_methods && delivery_methods.length > 0

  const handleSubmit = (data: FormData) => {
    const pickup_time = data.get('pickup_time') as string
    const fulfillment_time = data.get('fulfillment_time') as string
    const order_source = orderSource
    const delivery_method = deliveryMethod

    startTransition(async () => {
      try {
        // Update order properties first
        await updateOrderProperties({
          cartId,
          pickup_time,
          fulfillment_time,
          order_source,
          delivery_method,
        })

        // Call onSuccess callback if provided (e.g., to end sale/create order)
        if (onSuccess) {
          try {
            await onSuccess()
          } catch (orderError: any) {
            // Handle order creation errors separately
            const errorMessage = typeof orderError === 'string'
              ? orderError
              : orderError?.message || 'Failed to complete order'

            toast.error(errorMessage)
            console.error('Order creation failed:', orderError)
            // Don't close modal on order creation failure
            return
          }
        }

        // Only close modal if everything succeeded
        onClose?.()
      } catch (error: any) {
        // Handle order properties update errors
        const errorMessage = typeof error === 'string'
          ? error
          : error?.message || 'Failed to update order properties'

        toast.error(errorMessage)
        console.error('Failed to update order properties:', error)
        // Don't close modal on error
      }
    })
  }

  return (
    <form className="flex w-full flex-col gap-3 pt-2" action={handleSubmit}>
      {(isLoadingLocations || !isMetadataLoaded) && (
        <div className="rounded-md bg-yellow-50 border border-yellow-200 p-3 text-sm text-yellow-800 flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading order configuration data...
        </div>
      )}

      <div className="flex flex-col gap-1">
        <Label className="md:text-sm font-medium" htmlFor="pickup_time">Pickup Time</Label>
        <DateTimePicker
          id="pickup_time"
          name="pickup_time"
          value={pickupTime}
          onChange={(value) => {
            setPickupTime(value)
            userModifiedRef.current.pickupTime = true
          }}
          disabled={isPending}
          placeholder='Select Pickup Time'
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label className="md:text-sm font-medium" htmlFor="fulfillment_time">Fulfillment Time</Label>
        <DateTimePicker
          id="fulfillment_time"
          name="fulfillment_time"
          value={fulfillmentTime}
          onChange={(value) => {
            setFulfillmentTime(value)
            userModifiedRef.current.fulfillmentTime = true
          }}
          disabled={isPending}
          placeholder='Select Fulfillment Time'
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label className="md:text-sm font-medium" htmlFor="order_source">Order Source</Label>
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="w-full justify-between" disabled={!hasOrderSources}>
              <span className={cn(!orderSource && 'text-gray-600 font-normal')}>
                {orderSource
                  ? order_sources?.find(s => s.value === orderSource)?.label || orderSource
                  : !hasOrderSources
                    ? "No order sources available"
                    : "Select Order Source"
                }
              </span>
              <ChevronDown className="h-4 w-4 opacity-50" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            sideOffset={4}
            className="min-w-[var(--radix-dropdown-menu-trigger-width)] max-h-[300px] overflow-y-auto z-[100]"
          >
            <DropdownMenuRadioGroup
              value={orderSource}
              onValueChange={(value) => {
                setOrderSource(value)
                userModifiedRef.current.orderSource = true
              }}
            >
              {order_sources?.map((source) => (
                <DropdownMenuRadioItem key={source.value} value={source.value}>
                  {source.label}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="flex flex-col gap-1">
        <Label className="md:text-sm font-medium" htmlFor="delivery_method">Delivery Method</Label>
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="w-full justify-between" disabled={!hasDeliveryMethods}>
              <span className={cn(!deliveryMethod && 'text-gray-600 font-normal')}>
                {deliveryMethod
                  ? delivery_methods?.find(m => m.value === deliveryMethod)?.label || deliveryMethod
                  : !hasDeliveryMethods
                    ? "No delivery methods available"
                    : "Select Delivery Method"
                }
              </span>
              <ChevronDown className="h-4 w-4 opacity-50" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            sideOffset={4}
            className="min-w-[var(--radix-dropdown-menu-trigger-width)] max-h-[300px] overflow-y-auto z-[100]"
          >
            <DropdownMenuRadioGroup
              value={deliveryMethod}
              onValueChange={(value) => {
                setDeliveryMethod(value)
                userModifiedRef.current.deliveryMethod = true
              }}
            >
              {delivery_methods?.map((method) => (
                <DropdownMenuRadioItem key={method.value} value={method.value}>
                  {method.label}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="flex flex-col-reverse items-center gap-2 sm:flex-row pt-6">
        <Button
          variant="secondary"
          size="lg"
          className="w-full"
          onClick={onClose}
          type="button"
        >
          Cancel
        </Button>
        <Button
          variant="default"
          size="lg"
          className="w-full"
          disabled={isPending || !isMetadataLoaded || isLoadingLocations}
          type="submit"
        >
          {(isPending || isLoadingLocations) && <Loader2 className='animate-spin size-5' />}
          {!isPending && !isLoadingLocations && (
            <>
              <CheckCircle className="h-4 w-4" />
              Set Properties
            </>
          )}
        </Button>
      </div>
    </form >
  )
}
